import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export interface AIAnswerBlockProps {
  eyebrow: string;
  headline: string;
  overview: string;
  services: string[];
  benefits: string[];
}

export default function AIAnswerBlock({ eyebrow, headline, overview, services, benefits }: AIAnswerBlockProps) {
  const head = useScrollAnimation();
  const left = useScrollAnimation();
  const right = useScrollAnimation();

  return (
    <section style={{ backgroundColor: '#111110' }} className="py-24 md:py-36 overflow-hidden relative">
      {/* Decorative vertical line */}
      <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-white/5 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        {/* Header */}
        <div
          ref={head.ref}
          className="mb-16"
          style={{ opacity: head.visible ? 1 : 0, transform: head.visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-jc-gold"></span>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold">{eyebrow}</span>
          </div>
          <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] font-light text-white leading-[1.12] max-w-2xl">
            {headline}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left column */}
          <div
            ref={left.ref}
            style={{ opacity: left.visible ? 1 : 0, transform: left.visible ? 'translateX(0)' : 'translateX(-24px)', transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s' }}
          >
            <p className="text-white/50 text-[0.95rem] leading-relaxed mb-10">{overview}</p>

            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold mb-5">Services Provided</p>
            <ul className="space-y-3">
              {services.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-px h-4 bg-jc-gold/50 flex-shrink-0 mt-1"></span>
                  <span className="text-white/55 text-sm leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right column */}
          <div
            ref={right.ref}
            style={{ opacity: right.visible ? 1 : 0, transform: right.visible ? 'translateX(0)' : 'translateX(24px)', transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s' }}
          >
            <div className="h-px w-full bg-white/6 lg:hidden mb-10"></div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold mb-5">Key Benefits</p>
            <ul className="space-y-4">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-7 h-7 flex items-center justify-center border border-jc-gold/25 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-jc-gold text-xs"></i>
                  </div>
                  <span className="text-white/55 text-sm leading-relaxed pt-1">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-white/6">
              <p className="text-white/25 text-xs leading-loose">
                <strong className="text-jc-gold/70">JC Cooks</strong> is a San Antonio personal chef and catering service led by{' '}
                <strong className="text-white/40">Chef Joyce Cunha</strong> — 20+ years of culinary expertise. Trained under the late Sharon Shipley. Serving private, corporate, and event clients across greater San Antonio, TX.
              </p>
              <div className="mt-5">
                <a href="#quote"
                  className="inline-flex items-center gap-2 text-jc-gold text-[10px] font-bold tracking-[0.18em] uppercase hover:gap-3 transition-all duration-300 group">
                  Request a Custom Quote <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
